import {useEffect, useState} from 'react';

import {Box, ButtonBase, InputBase, Stack, Typography} from '@mui/material';

import {analyzeBrand, EMPTY_BRAND_PROFILE, ensureMetafieldDefinitions, saveBrandProfile} from './brandService';
import BrandProfileEditor from './BrandProfileEditor';

const C = {
    ink: 'var(--cg-text)',
    surface: 'var(--cg-surface)',
    surfaceAlt: 'var(--cg-surface-alt)',
    border: 'var(--cg-border)',
    border2: 'var(--cg-border-strong)',
    textSoft: 'var(--cg-text-soft)',
    textMuted: 'var(--cg-text-muted)',
    teal: '#1D9E75',
    tealL: '#E1F5EE',
    coral: '#D85A30',
    amber: '#BA7517',
};

const STEPS = ['basics', 'analyzing', 'review'];

const ANALYSIS_STEPS = [
    {label: 'Browsing website', icon: '🌐'},
    {label: 'Reading content & copy', icon: '📄'},
    {label: 'Extracting brand voice', icon: '🎯'},
    {label: 'Identifying keywords', icon: '🔍'},
    {label: 'Building profile', icon: '✨'},
];

function AnalyzingState({companyName}) {
    const [activeStep, setActiveStep] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveStep((s) => (s < ANALYSIS_STEPS.length - 1 ? s + 1 : s));
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    return (
        <Box sx={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: '20px',
            padding: '48px 40px',
        }}>
            <Typography sx={{
                fontFamily: "'Playfair Display',Georgia,serif",
                fontSize: 22,
                color: C.ink,
                textAlign: 'center',
                marginBottom: '6px',
            }}>
                Analyzing {companyName}
            </Typography>
            <Typography sx={{fontSize: 13, color: C.textSoft, textAlign: 'center', marginBottom: '32px'}}>
                AI is browsing your website and extracting brand identity
            </Typography>

            <Stack gap="6px" sx={{maxWidth: 320, margin: '0 auto'}}>
                {ANALYSIS_STEPS.map((s, i) => {
                    const done = i < activeStep;
                    const active = i === activeStep;
                    return (
                        <Stack
                            key={s.label}
                            direction="row"
                            alignItems="center"
                            gap="12px"
                            sx={{
                                padding: '10px 14px',
                                borderRadius: '10px',
                                background: active ? `${C.teal}0c` : 'transparent',
                                border: `1px solid ${active ? `${C.teal}33` : 'transparent'}`,
                                transition: 'all .3s ease',
                                opacity: done ? 0.5 : active ? 1 : 0.3,
                            }}>
                            <Box sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 14,
                                flexShrink: 0,
                                background: done ? `${C.teal}14` : active ? `${C.teal}14` : C.surfaceAlt,
                            }}>
                                {done ? <span style={{color: C.teal, fontSize: 12, fontWeight: 700}}>✓</span> : s.icon}
                            </Box>
                            <Typography sx={{
                                fontSize: 13,
                                fontWeight: active ? 600 : 400,
                                color: done ? C.textMuted : active ? C.ink : C.textFaint,
                            }}>
                                {s.label}
                            </Typography>
                            {active && (
                                <Box sx={{
                                    marginLeft: 'auto',
                                    width: 16,
                                    height: 16,
                                    border: `2px solid ${C.teal}`,
                                    borderTopColor: 'transparent',
                                    borderRadius: '50%',
                                    animation: 'spin 0.8s linear infinite',
                                }} />
                            )}
                        </Stack>
                    );
                })}
            </Stack>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </Box>
    );
}

export default function BrandSetupWizard({onComplete}) {
    const [step, setStep] = useState('basics');
    const [companyName, setCompanyName] = useState('');
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [error, setError] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    const [profile, setProfile] = useState(null);
    const [saving, setSaving] = useState(false);

    const canAnalyze = companyName.trim() && websiteUrl.trim();

    const startAnalysis = async () => {
        if (!canAnalyze) return;
        setStep('analyzing');
        setError('');

        try {
            setStatusMessage('AI is browsing your website and analyzing brand identity...');
            const result = await analyzeBrand(websiteUrl.trim(), companyName.trim());
            result.companyName = result.companyName || companyName.trim();

            setProfile(result);
            setStep('review');
        } catch (err) {
            setError(err.message || 'Analysis failed');
            setStep('basics');
        }
    };

    const skipAnalysis = () => {
        setProfile({
            ...EMPTY_BRAND_PROFILE,
            companyName: companyName.trim(),
            websiteUrl: websiteUrl.trim(),
        });
        setStep('review');
    };

    const handleSave = async (editedProfile) => {
        try {
            setError('');
            setSaving(true);
            const saved = await saveBrandProfile(editedProfile);
            // Create metafield definitions in Shopify for any mapped metafields
            if (saved.shopifyMapping) {
                ensureMetafieldDefinitions(saved.shopifyMapping).catch(() => {});
            }
            onComplete(saved);
        } catch (err) {
            setError(err.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            background: 'var(--cg-canvas)',
        }}>
            <Box sx={{
                width: '100%',
                maxWidth: step === 'review' ? 760 : 520,
                transition: 'max-width .3s ease',
            }}>
                {/* Step indicators */}
                <Stack direction="row" gap="8px" justifyContent="center" sx={{marginBottom: '32px'}}>
                    {STEPS.map((s) => (
                        <Box key={s} sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: s === step ? C.teal : `${C.textMuted}44`,
                            transition: 'background .2s',
                        }} />
                    ))}
                </Stack>

                {/* ── Step 1: Basics ── */}
                {step === 'basics' && (
                    <Box sx={{
                        background: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: '20px',
                        padding: '40px',
                    }}>
                        <Typography sx={{
                            fontFamily: "'Playfair Display',Georgia,serif",
                            fontSize: 28,
                            lineHeight: 1.2,
                            color: C.ink,
                            marginBottom: '8px',
                        }}>
                            Set up your brand
                        </Typography>
                        <Typography sx={{fontSize: 14, color: C.textSoft, lineHeight: 1.5, marginBottom: '28px'}}>
                            We'll analyze your website to learn your brand voice, keywords, and product knowledge.
                            This powers every AI generation going forward.
                        </Typography>

                        <Stack gap="16px">
                            <Box>
                                <Typography sx={{fontSize: 12, fontWeight: 700, color: C.textSoft, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '.04em'}}>
                                    Company name
                                </Typography>
                                <InputBase
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    placeholder="e.g. Okun Hardware"
                                    sx={{
                                        width: '100%',
                                        fontFamily: 'inherit',
                                        fontSize: 15,
                                        padding: '12px 14px',
                                        borderRadius: '12px',
                                        border: `1px solid ${C.border2}`,
                                        background: C.surfaceAlt,
                                    }}
                                />
                            </Box>
                            <Box>
                                <Typography sx={{fontSize: 12, fontWeight: 700, color: C.textSoft, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '.04em'}}>
                                    Website URL
                                </Typography>
                                <InputBase
                                    value={websiteUrl}
                                    onChange={(e) => setWebsiteUrl(e.target.value)}
                                    placeholder="https://your-store.com"
                                    sx={{
                                        width: '100%',
                                        fontFamily: 'inherit',
                                        fontSize: 15,
                                        padding: '12px 14px',
                                        borderRadius: '12px',
                                        border: `1px solid ${C.border2}`,
                                        background: C.surfaceAlt,
                                    }}
                                />
                            </Box>
                        </Stack>

                        {error && (
                            <Typography sx={{fontSize: 13, color: C.coral, marginTop: '12px'}}>{error}</Typography>
                        )}

                        <Stack direction="row" gap="12px" sx={{marginTop: '28px'}}>
                            <ButtonBase
                                onClick={startAnalysis}
                                disabled={!canAnalyze}
                                sx={{
                                    flex: 1,
                                    padding: '14px',
                                    borderRadius: '12px',
                                    background: canAnalyze ? C.ink : C.surfaceAlt,
                                    color: canAnalyze ? C.surface : C.textMuted,
                                    fontFamily: 'inherit',
                                    fontWeight: 700,
                                    fontSize: 14,
                                    cursor: canAnalyze ? 'pointer' : 'not-allowed',
                                    transition: 'background .15s, color .15s',
                                }}>
                                Analyze website
                            </ButtonBase>
                            <ButtonBase
                                onClick={skipAnalysis}
                                disabled={!companyName.trim()}
                                sx={{
                                    padding: '14px 20px',
                                    borderRadius: '12px',
                                    border: `1px solid ${C.border}`,
                                    background: 'transparent',
                                    color: C.textSoft,
                                    fontFamily: 'inherit',
                                    fontWeight: 600,
                                    fontSize: 14,
                                    cursor: companyName.trim() ? 'pointer' : 'not-allowed',
                                }}>
                                Skip, fill manually
                            </ButtonBase>
                        </Stack>
                    </Box>
                )}

                {/* ── Step 2: Analyzing ── */}
                {step === 'analyzing' && (
                    <AnalyzingState companyName={companyName} statusMessage={statusMessage} />
                )}

                {/* ── Step 3: Review & edit ── */}
                {step === 'review' && profile && (
                    <Box sx={{
                        background: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: '20px',
                        padding: '32px',
                    }}>
                        <Typography sx={{
                            fontFamily: "'Playfair Display',Georgia,serif",
                            fontSize: 24,
                            lineHeight: 1.2,
                            color: C.ink,
                            marginBottom: '6px',
                        }}>
                            Review your brand profile
                        </Typography>
                        <Typography sx={{fontSize: 13, color: C.textSoft, lineHeight: 1.5, marginBottom: '24px'}}>
                            We extracted this from your website. Edit anything that doesn't look right.
                        </Typography>

                        <BrandProfileEditor
                            profile={profile}
                            onChange={setProfile}
                        />

                        {error && (
                            <Typography sx={{fontSize: 13, color: C.coral, marginTop: '12px'}}>{error}</Typography>
                        )}

                        <Stack direction="row" gap="12px" sx={{marginTop: '24px'}}>
                            <ButtonBase
                                onClick={() => handleSave(profile)}
                                disabled={saving}
                                sx={{
                                    flex: 1,
                                    padding: '14px',
                                    borderRadius: '12px',
                                    background: C.teal,
                                    color: '#fff',
                                    fontFamily: 'inherit',
                                    fontWeight: 700,
                                    fontSize: 14,
                                    cursor: saving ? 'progress' : 'pointer',
                                    opacity: saving ? 0.8 : 1,
                                }}>
                                {saving ? 'Saving...' : 'Save & start'}
                            </ButtonBase>
                            <ButtonBase
                                onClick={() => { setStep('basics'); setProfile(null); }}
                                sx={{
                                    padding: '14px 20px',
                                    borderRadius: '12px',
                                    border: `1px solid ${C.border}`,
                                    background: 'transparent',
                                    color: C.textSoft,
                                    fontFamily: 'inherit',
                                    fontWeight: 600,
                                    fontSize: 14,
                                    cursor: 'pointer',
                                }}>
                                Start over
                            </ButtonBase>
                        </Stack>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
